import React, { Component } from "react";
import { get, isEqual, noop, sortBy } from "lodash";

import { formatPurse } from "@tvg/formatter/currency";
import { getRunner } from "@tvg/utils/getRaceAtiveRunner";

import { replaceCAPIVariables } from "@tvg/utils/capiUtils";
import PastPerformanceStatus from "@tvg/ipp/src/components/PastPerformanceStatus";
import { IppMatchConditionsMask } from "@tvg/ipp/src/components/__masks__/IppMatchConditions/ippMatchConditions";
import RunnerInfo from "@tvg/ipp/src/components/RunnerInfo";
import SimpleInlineInfo from "@tvg/ipp/src/components/SimpleInlineInfo";
import HorseOddsPP from "@tvg/ipp/src/HorseOddsPP";

import HorseSwitcher from "@tvg/ipp/src/components/HorseSwitcher";
import { ChangesCardProWrapper } from "@tvg/pp/src/components/Main/InfoNChangesPro/MainCard/styled-components";
import ChangesCardPro from "@tvg/pp/src/components/Main/InfoNChangesPro/ChangesCardPro";
import PastPerformanceList from "@tvg/ipp/src/components/PastPerformanceList";
import { Icon, ToastManager, bottomOffsets, Switch } from "@tvg/design-system";
import {
  emptyMessagesDefault,
  mtpNotificationConfigDefault,
  handicappingRunnerFlagsDefault
} from "../../__mocks__/ipp-pastRaces.mock";
import {
  hasChangesToShow,
  getSurfaceChangesKeys
} from "../../_organism/RunnerRaceChanges";
import Info from "../../_molecule/Info";
import MTPNotification from "../../_molecule/MTPNotification";
import { infoOutline } from "../../_static/Icons/icons";

import {
  ListHolder,
  ListWrapper,
  MatchRaces,
  MatchRacesTitle,
  MatchRacesInfoContainer,
  CardWrapper,
  EmptyStateWrapper,
  RunnerInfoHeader,
  MyStableRunnerInfo,
  MyStableRunnerInfoDob
} from "./styled-components";

const computeInfoMessage = (
  totalRaces,
  emptyMessageNone,
  emptyMessageWithFilterMultiple,
  emptyMessageWithFilterSingle
) => {
  if (totalRaces > 1) return `${totalRaces} ${emptyMessageWithFilterMultiple}`;
  if (totalRaces === 1) return emptyMessageWithFilterSingle;
  return emptyMessageNone;
};

export const renderEmpty = (
  emptyMessages,
  totalRaces,
  isMatchConditionsOn,
  onMatchToggle,
  isMobile = false
) => {
  const {
    emptyMessageNone,
    emptyMessageWithFilterSingle,
    emptyMessageWithFilterMultiple,
    emptyMessageTitle
  } = emptyMessages;
  const computedMessage = computeInfoMessage(
    totalRaces,
    emptyMessageNone,
    emptyMessageWithFilterMultiple,
    emptyMessageWithFilterSingle
  );

  return (
    <EmptyStateWrapper isMobile={isMobile}>
      <Info
        mainIcon={infoOutline}
        hasOverlayIcon={false}
        title={emptyMessageTitle}
        message={isMatchConditionsOn ? computedMessage : emptyMessageNone}
        hasButton={isMatchConditionsOn && totalRaces > 0}
        buttonText="Turn filter off"
        buttonHandler={onMatchToggle}
        full
      />
    </EmptyStateWrapper>
  );
};

export default class RunnersPastPerformance extends Component {
  static defaultProps = {
    races: [],
    selectedRace: -1,
    totalRaces: 0,
    matchQuantity: 0,
    runnerStatsTitle: "Past Performances",
    runnerStats: [],
    onRace: noop,
    onModalClose: noop,
    onMatchToggle: noop,
    isMatchConditionsOn: false,
    emptyMessage: "No replays are available for this horse.",
    borderless: false,
    isMobile: false,
    runners: [],
    raceTypeCode: "T",
    activeRunner: "",
    activeRunnerBI: null,
    onRunnerClick: noop,
    isLoadingRunnerPP: true,
    isLoadingStats: true,
    postRaceReportShown: false,
    currentRace: {},
    noRunnerCommentMessage: "",
    pastPerformanceMatchText: "",
    isSilkShown: true,
    emptyMessages: emptyMessagesDefault,
    handicappingRunnerFlags: handicappingRunnerFlagsDefault,
    mtpNotificationConfig: mtpNotificationConfigDefault,
    handicappingMTPNotification: false,
    isHorseSwitcherDisabled: false,
    onClickVideo: noop
  };

  shouldComponentUpdate(nextProps) {
    return (
      this.props.isMatchConditionsOn !== nextProps.isMatchConditionsOn ||
      this.props.totalRaces !== nextProps.totalRaces ||
      this.props.matchQuantity !== nextProps.matchQuantity ||
      this.props.activeRunner !== nextProps.activeRunner ||
      this.props.selectedRace !== nextProps.selectedRace ||
      this.props.races.length !== nextProps.races.length ||
      this.props.isLoadingRunnerPP !== nextProps.isLoadingRunnerPP ||
      this.props.isLoadingStats !== nextProps.isLoadingStats ||
      this.props.isSilkShown !== nextProps.isSilkShown ||
      this.props.isHorseSwitcherDisabled !==
        nextProps.isHorseSwitcherDisabled ||
      !isEqual(this.props.activeRunnerBI, nextProps.activeRunnerBI) ||
      !isEqual(this.props.runnerStats, nextProps.runnerStats) ||
      !isEqual(this.props.races, nextProps.races) ||
      !isEqual(this.props.currentRace, nextProps.currentRace)
    );
  }

  componentDidUpdate(prevProps) {
    const formattedRaceName = (raceId) => {
      const splittedName = raceId.split("-");
      return `${splittedName[0]} R${splittedName[1]}`;
    };

    const toastMessage = (mtp, raceId) => {
      if (mtp >= 1 && mtp <= 5) {
        return `${mtp} MTP - ${formattedRaceName(raceId)}`;
      }
      if (mtp < 1) {
        return "Race starting soon";
      }
      return null;
    };

    if (
      get(this.props.currentRace, "mtp") <= 5 &&
      get(this.props.currentRace.status, "code") !== "RO" &&
      get(this.props.currentRace.status, "code") !== "SK"
    ) {
      ToastManager.show({
        message: toastMessage(
          get(this.props.currentRace, "mtp"),
          get(this.props.currentRace, "id")
        ),
        variant: "warning",
        isFullWidth: false,
        type: "toast",
        icon: "exclamation",
        bottomOffset: bottomOffsets.TALL,
        autoHide: false,
        hasRoundedStyle: true,
        actionLink: <Icon name="arrowRight" size="s" lineColor="#FFF" />,
        onClick: () => {
          this.props.onModalClose();
          this.props.onClickVideo();
          ToastManager.hide();
        }
      });
    }

    if (
      (get(prevProps.currentRace.status, "code") !==
        get(this.props.currentRace.status, "code") &&
        get(this.props.currentRace.status, "code")) === "SK"
    ) {
      ToastManager.hide();
    }
  }

  buildRunners = (runners) => {
    const newRunners = [];
    if (runners.length > 0) {
      runners.forEach((bi) =>
        bi.runners.forEach((runner) =>
          newRunners.push({ ...bi, runners: [runner] })
        )
      );
    }
    return sortBy(newRunners, ["runners[0].scratched", "biNumber"]);
  };

  renderList = () =>
    this.props.isLoadingRunnerPP || this.props.races.length > 0 ? (
      <ListWrapper
        borderless={this.props.borderless}
        data-qa-label="ipp-fulllist"
      >
        <PastPerformanceList
          raceDistance={get(this.props.currentRace, "distance") || "-"}
          raceSurface={get(this.props.currentRace, "surface.name") || "-"}
          isLoading={this.props.isLoadingRunnerPP}
          races={this.props.races}
          selectedRace={this.props.selectedRace}
          onRace={this.props.onRace}
          isMobile={this.props.isMobile}
          postRaceReportShown={this.props.postRaceReportShown}
        />
      </ListWrapper>
    ) : (
      this.props.isMobile &&
      renderEmpty(
        this.props.emptyMessages,
        this.props.totalRaces,
        this.props.isMatchConditionsOn,
        this.props.onMatchToggle,
        this.props.isMobile
      )
    );

  renderMatchToggle = () => {
    const pastPerformanceMatchText = replaceCAPIVariables(
      this.props.pastPerformanceMatchText,
      {
        matchQuantity: String(this.props.matchQuantity),
        totalRaces: String(this.props.totalRaces)
      }
    );

    return this.props.isLoadingRunnerPP ? (
      <IppMatchConditionsMask />
    ) : (
      (this.props.races.length > 0 || this.props.isMatchConditionsOn) && (
        <MatchRaces>
          <MatchRacesInfoContainer>
            <MatchRacesTitle
              dangerouslySetInnerHTML={{ __html: pastPerformanceMatchText }}
            />
            <SimpleInlineInfo
              info={[
                {
                  key: "numRunners",
                  value: `${get(this.props.currentRace, "numRunners")} runners`
                },
                {
                  key: "distance",
                  value: `${get(this.props.currentRace, "distance") || "-"}`
                },
                {
                  key: "course",
                  value: `${get(this.props.currentRace, "surface.name") || "-"}`
                },
                {
                  key: "condition",
                  value: `${
                    get(this.props.currentRace, "surface.defaultCondition") ||
                    "-"
                  }`
                },
                {
                  key: "raceClass",
                  value: `${
                    get(this.props.currentRace, "raceClass.name") || "-"
                  }`
                },
                {
                  key: "purse",
                  value: formatPurse(get(this.props.currentRace, "purse"))
                }
              ]}
              changed={
                this.props.currentRace.changes
                  ? getSurfaceChangesKeys(this.props.currentRace.changes)
                  : []
              }
              useUpdatedIcon
            />
          </MatchRacesInfoContainer>
          <Switch
            isOn={this.props.isMatchConditionsOn}
            onPress={this.props.onMatchToggle}
            qaLabel="ipp-match-condition-button"
          />
        </MatchRaces>
      )
    );
  };

  renderHorseSwitcher = () =>
    this.props.isMobile && (
      <HorseSwitcher
        isDisabled={this.props.isHorseSwitcherDisabled}
        raceTypeCode={this.props.raceTypeCode}
        runners={this.buildRunners(this.props.runners)}
        activeRunner={this.props.activeRunner}
        onRunnerClick={this.props.onRunnerClick}
      />
    );

  renderHorseOdds = () =>
    this.props.activeRunnerBI && (
      <HorseOddsPP
        activeRunnerBI={this.props.activeRunnerBI}
        activeRunner={this.props.activeRunner}
        noRunnerCommentMessage={this.props.noRunnerCommentMessage}
        isSilkShown={this.props.isSilkShown}
      />
    );

  renderRunnerInfo = () => {
    const runner = getRunner(
      this.props.activeRunnerBI,
      this.props.activeRunner
    );
    const raceTypeCode = get(
      this.props.races,
      `[${this.props.selectedRace}].raceTypeCode`,
      "T"
    );
    const currentRaceTypeCode = get(this.props.currentRace, "type.code", "T");

    return runner ? (
      <CardWrapper>
        <RunnerInfo
          title={`${this.props.activeRunner}'s Info`}
          runner={runner}
          raceTypeCode={raceTypeCode}
          currentRaceTypeCode={currentRaceTypeCode}
          handicappingRunnerFlags={this.props.handicappingRunnerFlags}
        >
          {this.renderHorseOdds()}
        </RunnerInfo>
        {this.renderRunnerRaceChanges()}
      </CardWrapper>
    ) : (
      <CardWrapper data-qa-label="my-stable-runner-info">
        <RunnerInfoHeader>
          <MyStableRunnerInfo isBold data-qa-label="runner-info-title">
            {this.props.activeRunner}
          </MyStableRunnerInfo>
          <MyStableRunnerInfoDob data-qa-label="runner-info-dob">
            {this.props.runnerDob}
          </MyStableRunnerInfoDob>
        </RunnerInfoHeader>
      </CardWrapper>
    );
  };

  renderRunnerRaceChanges = () => {
    const runnerId = get(
      getRunner(this.props.activeRunnerBI, this.props.activeRunner),
      "runnerId"
    );

    const changes = {
      horse: get(this.props.currentRace, "changes.horse"),
      surface: get(this.props.currentRace, "changes.surface")
    };

    return runnerId && changes && hasChangesToShow(changes, runnerId) ? (
      <ChangesCardProWrapper hasMarginTop>
        <ChangesCardPro
          changes={changes}
          bettingInterests={this.props.currentRace.bettingInterests}
          isSeeProfileAndPPModal
          isFullWidth
        />
      </ChangesCardProWrapper>
    ) : null;
  };

  renderHeader = () =>
    (this.props.isLoadingStats ||
      this.props.races.length > 0 ||
      this.props.isMatchConditionsOn) && (
      <CardWrapper hasSeparator>
        <PastPerformanceStatus
          isLoading={this.props.isLoadingStats}
          title={this.props.runnerStatsTitle}
          races={get(this.props.runnerStats, "starts", 0)}
          wins={get(this.props.runnerStats, "wins", 0)}
          top3={get(this.props.runnerStats, "top3", 0)}
        />
      </CardWrapper>
    );

  renderMTPNotification = () =>
    this.props.handicappingMTPNotification && (
      <MTPNotification
        onModalClose={this.props.onModalClose}
        currentRace={this.props.currentRace}
        mtpNotificationConfig={this.props.mtpNotificationConfig}
      />
    );

  render() {
    return (
      <ListHolder>
        {this.renderHorseSwitcher()}
        {this.renderRunnerInfo()}
        {this.renderHeader()}
        {this.props.runners.length > 0 && this.renderMatchToggle()}
        {this.renderList()}
        {this.renderMTPNotification()}
      </ListHolder>
    );
  }
}
